import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AlertBox,
  Button,
  ChainSelector,
  Input,
  SelectOption,
  InputSelect,
  SvgIcon,
} from '@/components';
import { useInputText, useDebounce, useApiCall } from '@/hooks';
import { Validation } from '@/models';
import {
  ChainsEnum,
  AssetsEnum,
  ChainsDictionary,
  DEBOUNCE_DELAY,
} from '@/config';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { amlTraceSelectors, serviceOptionsSelectors } from '@/redux/selectors';
import { amlTraceActions } from '@/redux/actions';
import {
  amlbotAPI,
  WhichHashRequest,
  AssetTokenRequest,
  AssetTokenResponse,
} from '@/api';
import { TraceEntry } from '../TraceInvestigationForm/TraceInvestigationForm.types';

import styles from './TraceEntryForm.module.sass';

const DEFAULT_NETWORK_TOKEN_ID = '0';

interface TraceEntryFormProps {
  isDisabled: boolean;
  onConfirm: (entry: TraceEntry) => void;
  onCancel: () => void;
  canCancel: boolean;
  initialValue?: TraceEntry | null;
}

const TraceEntryForm = ({
  isDisabled,
  onConfirm,
  onCancel,
  canCancel,
  initialValue,
}: TraceEntryFormProps): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { data: serviceOptionData } = useAppSelector(
    serviceOptionsSelectors.getServiceOptionsSelectors,
  );

  const tracerCurrencies = useAppSelector(
    amlTraceSelectors.getTracerCurrencies,
  );

  const [assetTokens, setAssetTokens] = useState<SelectOption[]>([]);
  const [tokenFilter, setTokenFilter] = useState({ page: 1, term: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [hasMoreTokens, setHasMoreTokens] = useState(true);
  const [selectedToken, setSelectedToken] = useState<SelectOption | null>(
    initialValue && initialValue.tokenId !== DEFAULT_NETWORK_TOKEN_ID
      ? {
          value: initialValue.tokenId,
          label: initialValue.displayAsset,
          code: initialValue.displayAsset,
        }
      : null,
  );

  const [whichLoading, setWhichLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>(
    initialValue ? [initialValue.chain] : [],
  );

  const HASH_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
    ],
    [t],
  );

  const CHAIN_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
    ],
    [t],
  );

  const TOKEN_VALIDATIONS: Validation[] = useMemo(
    () => [
      {
        name: 'minLength',
        value: 0,
        message: t('translation.field_is_required'),
      },
    ],
    [t],
  );

  const hashField = useInputText(initialValue?.hash ?? '', HASH_VALIDATIONS);
  const chainField = useInputText(initialValue?.chain ?? '', CHAIN_VALIDATIONS);
  const tokenField = useInputText(
    initialValue?.tokenId ?? '',
    TOKEN_VALIDATIONS,
  );

  useEffect(() => {
    if (!tracerCurrencies?.length) {
      dispatch(amlTraceActions.fetchCurrencies());
    }
  }, [dispatch, tracerCurrencies]);

  useEffect(() => {
    tokenField.onChange('');
    setAssetTokens([]);
    setTokenFilter({ page: 1, term: '' });
  }, [chainField.value]);

  const baseChains = useMemo(() => {
    return tracerCurrencies.map((chain) => ({
      label: ChainsDictionary[chain].name,
      value: chain,
    }));
  }, [tracerCurrencies]);

  const [chainOptions, setChainOptions] = useState(baseChains);

  useEffect(() => {
    setChainOptions((prev) => {
      if (!suggestions.length) return baseChains;

      return baseChains.map((option) => ({
        ...option,
        isDisabled: !suggestions.includes(option.value as ChainsEnum),
      }));
    });
  }, [baseChains]);

  const resetOptions = useCallback(() => {
    setChainOptions(baseChains);
  }, [baseChains]);

  const debouncedRequest = useDebounce(async () => {
    if (!hashField.value) {
      resetOptions();
      return;
    }

    try {
      setWhichLoading(true);

      const payload: WhichHashRequest = {
        hash: hashField.value,
      };

      const response = await amlbotAPI.getWhichHash(payload);

      const {
        data: {
          suggestions: { txs: suggestedChains },
        },
      } = response;

      setSuggestions(suggestedChains);

      if (
        suggestedChains.length === 0 ||
        (chainField.value &&
          !suggestedChains.includes(chainField.value as ChainsEnum))
      ) {
        chainField.onChange('');
      }

      setChainOptions((prev) =>
        prev.map((option) => ({
          ...option,
          isDisabled: !suggestedChains.includes(option.value as ChainsEnum),
        })),
      );
    } catch (err: unknown) {
      console.error(err);
    } finally {
      setWhichLoading(false);
    }
  }, DEBOUNCE_DELAY);

  useEffect(() => {
    if (initialValue) {
      debouncedRequest();
    }
    // Only run once on mount, to hydrate suggestions/chainOptions when editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hashFieldChangeHandler = (newValue: string) => {
    hashField.onChange(newValue.trim());
    chainField.onReset();
    tokenField.onReset();
    debouncedRequest();
  };

  const [getAssetTokens] = useApiCall(async () => {
    const payload: AssetTokenRequest = {
      asset: chainField.value,
      page: tokenFilter.page,
      term: tokenFilter.term,
    };
    const response: AssetTokenResponse = await amlbotAPI.fetchTokens(payload);

    if (response.success) {
      setHasMoreTokens(!!response.data?.hasMore);

      const tokenList = (response.data?.result || []).map(
        (token): SelectOption => ({
          value: `${token.token_id}`,
          label: `${token.code} [${token.issuer}]`,
          code: token.code,
        }),
      );

      if (response.data?.page === 1) {
        setAssetTokens(() => [
          {
            value: `0`,
            label: `${ChainsDictionary[chainField.value].symbol} [${t(
              'translation.native_coin',
            )}]`,
            code: chainField.value,
          },
          ...(selectedToken ? [selectedToken] : []),
          ...tokenList,
        ]);
      } else {
        const uniqTokens = selectedToken
          ? tokenList.filter((token) => selectedToken.value !== token.value)
          : tokenList;
        setAssetTokens((currentOptions) => [...currentOptions, ...uniqTokens]);
      }
    }
  });

  useEffect(() => {
    if (
      !serviceOptionData?.hasTokensCoins.includes(chainField.value) ||
      !hasMoreTokens ||
      suggestions.length === 0
    ) {
      return;
    }

    getAssetTokens();
  }, [chainField.value, tokenFilter.page, tokenFilter.term]);

  const buildEntry = (
    chainValue: string,
    tokenId: string,
    tokenOption: SelectOption | null,
  ): TraceEntry => ({
    id: initialValue?.id ?? crypto.randomUUID(),
    hash: hashField.value,
    chain: chainValue as ChainsEnum,
    tokenId,
    displayAsset: tokenOption?.code || ChainsDictionary[chainValue].symbol,
    networkName: ChainsDictionary[chainValue].name,
  });

  const onChainChanged = (value: string) => {
    chainField.onChange(value);
    tokenField.onReset();
    setSelectedToken(null);

    const chainHasTokens = !!serviceOptionData?.hasTokensCoins.includes(
      value as ChainsEnum,
    );

    if (
      !chainHasTokens &&
      hashField.isValid &&
      value &&
      suggestions.length > 0 &&
      !whichLoading
    ) {
      onConfirm(buildEntry(value, DEFAULT_NETWORK_TOKEN_ID, null));
    }
  };

  const searchTermChanged = useDebounce(() => {
    setTokenFilter({ page: 1, term: searchTerm });
    setHasMoreTokens(true);
  });

  const tokenTermChanged = (val: string) => {
    setSearchTerm(val);
    searchTermChanged();
  };

  const handleScrollBottom = () => {
    if (hasMoreTokens) {
      setTokenFilter({ ...tokenFilter, page: tokenFilter.page + 1 });
    }
  };

  const handleTokenOnChange = (val: string) => {
    const tokenOption =
      val !== DEFAULT_NETWORK_TOKEN_ID
        ? assetTokens.find((option) => option.value === val) || null
        : null;

    setSelectedToken(tokenOption);
    tokenField.onChange(val);

    if (
      val &&
      hashField.isValid &&
      chainField.isValid &&
      suggestions.length > 0 &&
      !whichLoading
    ) {
      onConfirm(buildEntry(chainField.value, val, tokenOption));
    }
  };

  return (
    <div className={styles.entryForm}>
      <div className={styles.field}>
        <Input
          id="ai-investigation-hash"
          name="ai-investigation-hash"
          type="text"
          value={hashField.value}
          placeholder={t('translation.trace_tx_hash')!}
          onChange={hashFieldChangeHandler}
          onBlur={hashField.onBlur}
          isError={hashField.isDirty && !hashField.isValid}
          readOnly={whichLoading}
          disabled={isDisabled}
          clearable
          required
        />
        {hashField.isDirty && !hashField.isValid && (
          <AlertBox type="danger" small>
            {{ description: hashField.errors[0] }}
          </AlertBox>
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.sectionLabel}>
          {t('translation.ai_investigation_specify_blockchain')}
        </div>
        <ChainSelector
          list={chainOptions}
          onSelect={(val) => onChainChanged(val)}
          limited={false}
          isDisabled={isDisabled}
          modeChains={[]}
          className={styles.chainList}
        />
      </div>

      {serviceOptionData?.hasTokensCoins.includes(chainField.value) &&
        suggestions.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionLabel}>
              {t('translation.ai_investigation_choose_token')}
            </div>
            <div className={styles.fields}>
              <div className={styles.tokens}>
                <InputSelect
                  id="tokens"
                  name="tokens"
                  classNamePrefix="chain-select"
                  value={tokenField.value}
                  onChange={handleTokenOnChange}
                  onBlur={tokenField.onBlur}
                  options={assetTokens}
                  placeholder="Select"
                  isSearchable
                  clearable
                  disabled={whichLoading}
                  onMenuScrollToBottom={handleScrollBottom}
                  onInputChange={tokenTermChanged}
                  inputValue={searchTerm}
                />
              </div>
            </div>
          </div>
        )}

      {canCancel && (
        <div className={styles.actions}>
          <Button
            corner="rect"
            size="s"
            type="bordered"
            className={styles.delete}
            onClick={onCancel}
          >
            <SvgIcon name="delete" />
            {t('translation.delete_btn')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TraceEntryForm;
